import { useEffect, useState } from "react";
import { LessonArticle } from "../components/Lesson";
import { LessonFeedback } from "../components/LessonFeedback";
import { WalkthroughStep } from "../components/Walkthrough";
import { ExternalView } from "../components/ExternalTraining";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, ClipboardCheck, Download, FileText, ListChecks, PenLine, Award, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, download, openFile, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useApi, Loading, ErrorNote, StatusChip, btn, inputCls, Empty } from "../components/kit";

export function SectionView({ sec, n, done, onDone, canAct, start = 0, onLesson, aid }) {
  const total = sec.lessons.length;
  const [li, setLi] = useState(Math.max(0, Math.min(start, total - 1)));
  const last = li >= total - 1;
  const go = (i) => { setLi(i); window.scrollTo({ top: 0 }); onLesson && onLesson(i); };
  const navBtn = "tap inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-4 font-lesson font-medium disabled:opacity-30";
  return (
    <div className="lesson-paper -mx-4 border-y border-black/5 px-4 py-8 sm:mx-0 sm:rounded-3xl sm:border sm:px-12 sm:py-12">
      <header className="mb-10">
        <p className="lesson-eyebrow">Section {n}</p>
        <h2 className="mt-2 font-lesson-serif text-3xl sm:text-4xl" data-testid="section-title">{sec.title.replace(/^\d+\.\s*/, "")}</h2>
        {total > 1 && <div className="mt-6" data-testid="lesson-progress">
          <div className="mb-2 flex justify-between font-lesson text-sm text-[color:var(--lesson-mute)]"><span data-testid="lesson-progress-label">Lesson {li + 1} of {total}</span><span>{Math.round(((li + 1) * 100) / total)}%</span></div>
          <div className="h-1.5 overflow-hidden rounded-full bg-black/10" role="progressbar" aria-valuemin={1} aria-valuemax={total} aria-valuenow={li + 1}>
            <div className="h-full rounded-full bg-[#263229] transition-[width] duration-500" style={{ width: `${((li + 1) * 100) / total}%` }} /></div>
        </div>}
      </header>
      {total > 0 && <div key={li} className="fade-up"><LessonArticle l={sec.lessons[li]} n={li + 1} /></div>}
      <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-black/10 pt-8">
        {li > 0 && <button className={navBtn} onClick={() => go(li - 1)} data-testid="lesson-prev-button"><ArrowLeft className="h-4 w-4" /> Back</button>}
        {!last && <button className="kc-btn !mt-0 inline-flex items-center gap-2" onClick={() => go(li + 1)} data-testid="lesson-next-button">Next lesson <ArrowRight className="h-4 w-4" /></button>}
        {last && canAct && (done ? <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> Section complete</p>
          : <button className="kc-btn !mt-0" onClick={onDone} data-testid="section-complete-button">Mark section complete</button>)}
        {canAct && aid && total > 0 && <LessonFeedback aid={aid} sid={sec.id} li={li} title={sec.lessons[li]?.title} />}
      </div>
    </div>
  );
}

function QuizView({ data, onSubmitted, canAct, onNext, left = [] }) {
  const { version, attempts, assignment } = data;
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = version.quiz.questions;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/assignments/${assignment.id}/quiz`, { answers });
      setResult(r.data);
      toast[r.data.passed ? "success" : "error"](r.data.passed ? `Passed with ${r.data.score}%` : `${r.data.score}% — pass mark is ${r.data.pass_mark}%. You can try again.`);
      onSubmitted();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const fb = (qid) => result?.feedback.find((f) => f.question_id === qid);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Scenario quiz</h2>
        <p className="text-muted-foreground">Pass mark {version.quiz.pass_mark}%. Every attempt is kept. {assignment.quiz_passed && <b className="text-emerald-700">You have passed.</b>}</p>
      </div>
      {qs.map((q, i) => (
        <fieldset key={q.id} className="rounded-2xl border bg-white p-5" data-testid={`quiz-question-${i}`}>
          <legend className="sr-only">Question {i + 1}</legend>
          <p className="font-semibold mb-3">{i + 1}. {q.prompt}</p>
          <div className="grid gap-2">
            {q.options.map((o, j) => (
              <label key={j} className={`tap flex items-center gap-3 rounded-lg border px-4 cursor-pointer ${answers[q.id] === j ? "border-[color:var(--ink)] bg-muted" : ""}`}>
                <input type="radio" name={q.id} checked={answers[q.id] === j} onChange={() => setAnswers({ ...answers, [q.id]: j })}
                  disabled={!canAct || assignment.status === "completed"} data-testid={`quiz-q${i}-option-${j}`} className="h-5 w-5 accent-[color:var(--dcbc-red)]" />
                {o}
              </label>
            ))}
          </div>
          {fb(q.id) && (
            <p className={`mt-3 rounded-lg p-3 text-sm ${fb(q.id).correct ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid={`quiz-feedback-${i}`}>
              <b>{fb(q.id).correct ? "Correct." : "Not quite."}</b> {fb(q.id).explanation}
            </p>
          )}
        </fieldset>
      ))}
      {(assignment.quiz_passed || result?.passed) ? (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="quiz-passed-panel">
          <div className="flex-1"><p className="font-semibold text-emerald-900">You passed the quiz{result?.score != null ? ` with ${result.score}%` : ""}. Nice work.</p>
            {left.length > 0 && assignment.status !== "completed" && <p className="text-sm text-emerald-900" data-testid="quiz-left">To finish the course, still complete: {left.join(", ")}.</p>}</div>
          <button className={btn.primary} onClick={onNext} data-testid="quiz-continue-button">Continue <ArrowRight className="h-4 w-4" /></button>
        </div>
      ) : canAct && assignment.status !== "completed" && (
        <button className={btn.primary} disabled={busy || Object.keys(answers).length < qs.length} onClick={submit} data-testid="quiz-submit-button">
          {result ? "Submit another attempt" : "Submit answers"}
        </button>
      )}
      {attempts.length > 0 && (
        <div className="rounded-xl border bg-white p-4" data-testid="quiz-attempts">
          <p className="font-semibold mb-2">Your attempts</p>
          <ul className="text-sm space-y-1">{attempts.map((a) => (
            <li key={a.id} className="flex justify-between"><span>{fmtDateTime(a.at)}</span><span className={a.passed ? "text-emerald-700 font-semibold" : "text-rose-700"}>{a.score}% · {a.passed ? "Passed" : "Not passed"}</span></li>
          ))}</ul>
        </div>
      )}
    </div>
  );
}

function AckCard({ p, aid, user, reload, canAct }) {
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const slug = p.title.toLowerCase().replace(/[^a-z]+/g, "-");
  const open = async () => {
    try { await openFile(p.version.file_id); setTimeout(reload, 800); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/assignments/${aid}/acknowledge`, { policy_id: p.policy_id, checked, typed_name: name });
      toast.success(r.data.completed ? "Acknowledged — course complete!" : "Acknowledgement recorded.");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border bg-white p-5" data-testid={`ack-card-${slug}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <h3 className="font-semibold text-lg">{p.title}</h3>
        {p.acknowledged ? <StatusChip status="completed" label="Acknowledged" /> : <StatusChip status="not_started" label="Not acknowledged" />}
      </div>
      {!p.version ? <Empty>This policy hasn't been published yet.</Empty> : p.acknowledged ? (
        <p className="text-sm text-muted-foreground">Version {p.acknowledged.policy_version} acknowledged {fmtDateTime(p.acknowledged.at)} as “{p.acknowledged.typed_name}”.</p>
      ) : (
        <div className="space-y-4">
          <button onClick={open} className={btn.ghost} data-testid={`ack-open-${slug}`}><FileText className="h-4 w-4" /> Open version {p.version.version} {p.opened && "(opened)"}</button>
          {canAct && <>
            <label className="flex items-start gap-3 text-[15px]">
              <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} disabled={!p.opened}
                data-testid={`ack-check-${slug}`} className="mt-1 h-5 w-5 accent-[color:var(--dcbc-red)]" />
              <span>{p.wording}</span>
            </label>
            <div>
              <label htmlFor={`name-${slug}`} className="text-sm font-semibold">Type your full name: <span className="font-normal text-muted-foreground">{user.name}</span></label>
              <input id={`name-${slug}`} className={inputCls} value={name} onChange={(e) => setName(e.target.value)} disabled={!p.opened} data-testid={`ack-name-${slug}`} />
            </div>
            {!p.opened && <p className="text-sm text-amber-800">Open the document first to unlock the acknowledgement.</p>}
            <button className={btn.primary} disabled={busy || !checked || !name || !p.opened} onClick={submit} data-testid={`ack-submit-${slug}`}><PenLine className="h-4 w-4" /> Acknowledge</button>
          </>}
        </div>
      )}
    </div>
  );
}

export default function CoursePlayer() {
  const { aid } = useParams();
  const { user } = useAuth();
  const { data, loading, error, reload } = useApi(`/assignments/${aid}`);
  const [step, setStep] = useState(null);
  useEffect(() => {
    if (!data || step !== null) return;
    const secs = data.version.sections;
    const i = secs.findIndex((s) => s.id === data.assignment.last_section_id);
    const wt = data.version.signoff?.required ? 1 : 0;
    const base = secs.length + (data.version.quiz?.questions?.length ? 1 : 0) + (data.policies?.length ? 1 : 0);
    setStep(data.assignment.status === "completed" ? base + wt : data.assignment.awaiting_signoff ? base : i > 0 ? i : 0);
  }, [data, step]);
  if (error) return <ErrorNote error={error} />;
  if ((loading && !data) || step === null) return <Loading />;
  const { assignment: a, version: v, policies, completion } = data;
  const is_owner = data.is_owner && a.status !== "cancelled";
  if (v.kind === "external") return <ExternalView a={{ ...a, state: a.state || (a.cert_pending ? "awaiting_review" : a.status) }} v={v} own={is_owner} reload={reload} />;
  const steps = [
    ...v.sections.map((s) => ({ kind: "sec", title: s.title, sec: s, done: a.sections_done.includes(s.id) })),
    ...(v.quiz.questions.length ? [{ kind: "quiz", title: "Scenario quiz", done: a.quiz_passed }] : []),
    ...(policies.length ? [{ kind: "ack", title: "Policy acknowledgements", done: policies.every((p) => p.acknowledged) }] : []),
    ...(v.signoff?.required ? [{ kind: "wt", title: "Manager sign-off", done: !!a.walkthrough }] : []),
    { kind: "done", title: "Completion record", done: a.status === "completed" },
  ];
  const cur = steps[Math.min(step, steps.length - 1)];
  const markDone = async (sid) => {
    try {
      const r = await api.post(`/assignments/${aid}/sections/${sid}/complete`);
      if (r.data.completed) toast.success("Course complete!");
      await reload();
      const nx = Math.min(step + 1, steps.length - 1);
      setStep(nx);
      if (steps[nx].kind === "sec") savePos(steps[nx].sec.id, a.lesson_pos?.[steps[nx].sec.id] || 0);
      window.scrollTo({ top: 0 });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const icons = { sec: Circle, quiz: ListChecks, ack: PenLine, wt: ClipboardCheck, done: Award };
  const savePos = (sid, lesson) => is_owner && api.post(`/assignments/${aid}/position`, { section_id: sid, lesson }).catch(() => {});
  const pick = (i) => { setStep(i); window.scrollTo({ top: 0 }); if (steps[i].kind === "sec") savePos(steps[i].sec.id, a.lesson_pos?.[steps[i].sec.id] || 0); };
  return (
    <div>
      <Link to="/training" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"><ArrowLeft className="h-4 w-4" /> My Training</Link>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-3xl font-semibold" data-testid="course-title">{v.title}</h1>
        <span className="text-sm text-muted-foreground">Version {v.version}</span>
        <StatusChip status={a.state} />
      </div>
      {a.status === "cancelled" && <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900" data-testid="course-cancelled-note">This training was cancelled{a.cancel_reason ? ` (${a.cancel_reason})` : ""}. You can read it, but nothing more needs to be done.</p>}
      {!data.is_owner && <p className="mb-4 rounded-lg bg-sky-50 p-3 text-sm text-sky-900">You are viewing someone else's training. Only they can complete sections, take the quiz or acknowledge policies.</p>}
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <ol className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible pb-2" aria-label="Course steps">
          {steps.map((s, i) => {
            const Icon = s.done ? CheckCircle2 : icons[s.kind];
            return (
              <li key={i} className="shrink-0">
                <button onClick={() => pick(i)} data-testid={`course-step-${i}`} aria-current={i === step}
                  className={`tap flex w-full items-center gap-3 rounded-lg border px-3 text-left text-sm font-medium ${i === step ? "bg-[color:var(--ink)] text-white border-transparent" : "bg-white"}`}>
                  <Icon className={`h-4 w-4 shrink-0 ${s.done && i !== step ? "text-emerald-600" : ""}`} aria-hidden="true" />
                  <span className="lg:whitespace-normal whitespace-nowrap">{s.title}</span>
                  <span className="sr-only">{s.done ? "(done)" : ""}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <div className="min-w-0 fade-up" key={step}>
          {cur.kind === "sec" && <SectionView sec={cur.sec} n={step + 1} done={cur.done} canAct={is_owner} start={a.lesson_pos?.[cur.sec.id] || 0} onLesson={(i) => { a.lesson_pos = { ...(a.lesson_pos || {}), [cur.sec.id]: i }; savePos(cur.sec.id, i); }} onDone={() => markDone(cur.sec.id)} aid={aid} />}
          {cur.kind === "quiz" && <QuizView data={data} onSubmitted={reload} canAct={is_owner} left={steps.filter((s) => !s.done && !["quiz", "done"].includes(s.kind)).map((s) => s.title)}
            onNext={() => { const k = steps.findIndex((s) => !s.done && s.kind !== "quiz"); pick(k >= 0 ? k : steps.length - 1); }} />}
          {cur.kind === "ack" && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Policy acknowledgements</h2>
              <p className="text-muted-foreground">Open each document, tick the box and type your name. Managers can never acknowledge for you.</p>
              {policies.map((p) => <AckCard key={p.policy_id} p={p} aid={aid} user={user} reload={reload} canAct={is_owner} />)}
            </div>
          )}
          {cur.kind === "wt" && <WalkthroughStep a={a} v={v} />}
          {cur.kind === "done" && (
            <div className="rounded-2xl border bg-white p-8 text-center" data-testid="completion-panel">
              {a.status === "completed" ? (<>
                <Award className="mx-auto h-12 w-12 text-[color:var(--dcbc-red)]" />
                <h2 className="mt-3 text-2xl font-semibold">Course complete</h2>
                <p className="text-muted-foreground mt-1">Completed {fmtDateTime(a.completed_at)}{a.best_score != null && ` · score ${a.best_score}%`}</p>
                <button className={`${btn.primary} mt-6`} data-testid="download-completion-pdf"
                  onClick={() => download(`/completions/${a.completion_id}/pdf`, "DCBC_completion.pdf").catch((e) => toast.error(errMsg(e)))}>
                  <Download className="h-4 w-4" /> Download completion PDF
                </button>
                {a.walkthrough?.signoff_id && (
                  <button className={`${btn.ghost} mt-3 sm:mt-6 sm:ml-3`} data-testid="download-walkthrough-report"
                    onClick={() => download(`/signoffs/${a.walkthrough.signoff_id}/pdf`, `DCBC_walkthrough_${a.walkthrough.observed_date}.pdf`).catch((e) => toast.error(errMsg(e)))}>
                    <Download className="h-4 w-4" /> Walkthrough report
                  </button>
                )}
                {completion?.certificate && (
                  <button className={`${btn.dark} mt-3 sm:mt-6 sm:ml-3`} data-testid="download-welcome-certificate"
                    onClick={() => download(`/completions/${a.completion_id}/certificate`, "DCBC_Welcome_Certificate.pdf").catch((e) => toast.error(errMsg(e)))}>
                    <Award className="h-4 w-4" /> Download your welcome certificate
                  </button>
                )}
              </>) : (<>
                <h2 className="text-2xl font-semibold">Not complete yet</h2>
                <ul className="mt-4 inline-block text-left space-y-2">
                  {steps.slice(0, -1).map((s, i) => (
                    <li key={i} className="flex items-center gap-2">{s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4" />} {s.title}</li>
                  ))}
                </ul>
              </>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
